import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Wallet, ArrowDownLeft, ArrowUpRight, Clock, Copy, Check, Landmark, Banknote, Receipt, ChevronRight, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { walletApi, withdrawalApi, type WalletResponse, type LedgerEntryResponse, type WithdrawalResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";
import { useAuth } from "@/context/AuthContext";

export const Route = createFileRoute("/_user/wallet")({
  component: WalletPage,
});

export function WalletPage() {
  const { isAuthenticated } = useAuth();
  const [wallet, setWallet] = useState<WalletResponse | null>(null);
  const [ledger, setLedger] = useState<LedgerEntryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [amount, setAmount] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawals, setWithdrawals] = useState<WithdrawalResponse[]>([]);
  const [setupPinOpen, setSetupPinOpen] = useState(false);
  const [setupPin, setSetupPin] = useState("");
  const [confirmSetupPin, setConfirmSetupPin] = useState("");
  const [showSetupPin, setShowSetupPin] = useState(false);
  const [settingUpPin, setSettingUpPin] = useState(false);

  const [updatePinOpen, setUpdatePinOpen] = useState(false);
  const [oldPin, setOldPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmNewPin, setConfirmNewPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [updatingPin, setUpdatingPin] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadData();
  }, [isAuthenticated]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [w, l, wd] = await Promise.all([walletApi.getWallet(), walletApi.getLedger(), withdrawalApi.list()]);
      setWallet(w);
      setLedger(l);
      setWithdrawals(wd);
    } catch {
      toast.error("Failed to load wallet data");
    } finally {
      setLoading(false);
    }
  };

  const copyAccount = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard");
  };

  const handleWithdraw = async () => {
    const amt = Number(amount);
    const minWithdrawal = 5;
    if (!amt || amt < minWithdrawal) { toast.error(`Minimum withdrawal is N${minWithdrawal}`); return; }
    if (!accountName.trim() || !accountNumber.trim() || !bankName.trim()) { toast.error("Please fill all bank details"); return; }
    if (!pin || pin.length < 4) { toast.error("Please enter your PIN"); return; }

    setWithdrawing(true);
    try {
      await withdrawalApi.create({
        amount: amt,
        accountName: accountName.trim(),
        accountNumber: accountNumber.trim(),
        bankName: bankName.trim(),
        pin,
      });
      toast.success("Withdrawal request submitted");
      setWithdrawOpen(false);
      setAmount(""); setAccountName(""); setAccountNumber(""); setBankName(""); setPin("");
      loadData();
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Withdrawal failed";
      toast.error(msg);
    } finally {
      setWithdrawing(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <Wallet className="size-12 text-muted-foreground/30 mx-auto mb-3" />
        <h2 className="font-display text-xl font-semibold">Please sign in</h2>
        <p className="text-sm text-muted-foreground mt-1">You need to be logged in to view your wallet.</p>
      </div>
    );
  }

  return (
    <section className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <h1 className="font-display text-2xl font-bold mb-6">My Wallet</h1>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading wallet…</div>
      ) : (
        <>
          {/* Balance cards */}
          <div className="grid gap-4 sm:grid-cols-3 mb-8">
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <p className="text-xs text-muted-foreground mb-1">Available Balance</p>
              <p className="font-display text-2xl font-bold text-primary">{formatNaira(wallet?.availableBalance ?? 0)}</p>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <p className="text-xs text-muted-foreground mb-1">Held in Escrow</p>
              <p className="font-display text-2xl font-bold text-amber-600">{formatNaira(wallet?.heldBalance ?? 0)}</p>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <p className="text-xs text-muted-foreground mb-1">Total Balance</p>
              <p className="font-display text-2xl font-bold">{formatNaira(wallet?.balance ?? 0)}</p>
            </div>
          </div>

          {/* Dedicated bank account */}
          {wallet?.dedicatedBank && (
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Landmark className="size-5 text-primary" />
                <h3 className="font-semibold text-sm">Your Dedicated Virtual Account</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Transfer money to this account to top up your wallet. The system will automatically detect the payment.
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                <div className="rounded-lg bg-accent/30 px-3 py-2">
                  <p className="text-[10px] text-muted-foreground uppercase">Bank</p>
                  <p className="text-sm font-medium">{wallet.dedicatedBank.bankName}</p>
                </div>
                <div className="rounded-lg bg-accent/30 px-3 py-2">
                  <p className="text-[10px] text-muted-foreground uppercase">Account Name</p>
                  <p className="text-sm font-medium">{wallet.dedicatedBank.accountName}</p>
                </div>
                <div className="rounded-lg bg-accent/30 px-3 py-2 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Account Number</p>
                    <p className="text-sm font-medium font-mono">{wallet.dedicatedBank.accountNumber}</p>
                  </div>
                  <button onClick={() => copyAccount(wallet.dedicatedBank!.accountNumber)} className="text-primary hover:text-primary/80">
                    {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* PIN setup / update */}
          <div className="mb-4 flex flex-wrap gap-2">
            {!wallet?.hasPin ? (
              <Button onClick={() => setSetupPinOpen(true)} variant="outline" className="rounded-full">
                <Lock className="mr-2 size-4" /> Set Up Withdrawal PIN
              </Button>
            ) : (
              <Button onClick={() => setUpdatePinOpen(true)} variant="ghost" className="rounded-full text-xs">
                <Lock className="mr-2 size-4" /> Change PIN
              </Button>
            )}
          </div>

          {/* Withdraw button — all authenticated users */}
          <div className="mb-8">
            <Button onClick={() => setWithdrawOpen(o => !o)} variant="outline" className="rounded-full" disabled={!wallet?.hasPin}>
              <Banknote className="mr-2 size-4" /> Withdraw Funds
            </Button>
            {!wallet?.hasPin && (
              <p className="mt-2 text-xs text-amber-600">You must set up a withdrawal PIN before withdrawing.</p>
            )}
          </div>

          {/* Withdraw form */}
          {withdrawOpen && (
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft mb-8 space-y-4">
              <h3 className="font-semibold text-sm">Withdraw to Bank Account</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Amount (min N5)</Label>
                  <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="5000" />
                  {Number(amount) > 0 && (
                    <div className="rounded-lg bg-accent/30 px-3 py-2 text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Gross amount</span>
                        <span className="font-medium">{formatNaira(Number(amount))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Platform fee (deducted)</span>
                        <span className="font-medium text-amber-600">- {formatNaira(Number(amount) * (wallet?.withdrawalFeeRate ?? 0.02))}</span>
                      </div>
                      <div className="flex justify-between border-t border-border/40 pt-1">
                        <span className="font-semibold">You receive</span>
                        <span className="font-bold text-primary">{formatNaira(Number(amount) * (1 - (wallet?.withdrawalFeeRate ?? 0.02)))}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Bank Name</Label>
                  <Input value={bankName} onChange={e => setBankName(e.target.value)} placeholder="GTBank" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Account Name</Label>
                  <Input value={accountName} onChange={e => setAccountName(e.target.value)} placeholder="John Doe" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Account Number</Label>
                  <Input value={accountNumber} onChange={e => setAccountNumber(e.target.value)} placeholder="0123456789" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">PIN</Label>
                  <Input type="password" maxLength={6} value={pin} onChange={e => setPin(e.target.value)} placeholder="Your withdrawal PIN" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleWithdraw} disabled={withdrawing || !wallet?.hasPin} className="rounded-full">{withdrawing ? "Submitting…" : "Submit Request"}</Button>
                <Button variant="ghost" onClick={() => setWithdrawOpen(false)} className="rounded-full">Cancel</Button>
              </div>
            </div>
          )}

          {/* Set Up PIN Dialog */}
          <Dialog open={setupPinOpen} onOpenChange={setSetupPinOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Set Up Withdrawal PIN</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">PIN</Label>
                  <div className="relative">
                    <Input
                      type={showSetupPin ? "text" : "password"}
                      inputMode="numeric"
                      maxLength={6}
                      value={setupPin}
                      onChange={e => setSetupPin(e.target.value.replace(/\D/g, ""))}
                      placeholder="4–6 digits"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSetupPin(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showSetupPin ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Confirm PIN</Label>
                  <Input
                    type={showSetupPin ? "text" : "password"}
                    inputMode="numeric"
                    maxLength={6}
                    value={confirmSetupPin}
                    onChange={e => setConfirmSetupPin(e.target.value.replace(/\D/g, ""))}
                    placeholder="Repeat your PIN"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={async () => {
                      if (setupPin.length < 4) { toast.error("PIN must be at least 4 digits"); return; }
                      if (setupPin !== confirmSetupPin) { toast.error("PINs do not match"); return; }
                      setSettingUpPin(true);
                      try {
                        await walletApi.setupPin(setupPin);
                        toast.success("PIN set up successfully");
                        setSetupPinOpen(false);
                        setSetupPin(""); setConfirmSetupPin(""); setShowSetupPin(false);
                        loadData();
                      } catch (err) {
                        const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Failed to set PIN";
                        toast.error(msg);
                      } finally {
                        setSettingUpPin(false);
                      }
                    }}
                    disabled={settingUpPin}
                    className="rounded-full"
                  >
                    {settingUpPin ? "Setting up…" : "Set PIN"}
                  </Button>
                  <Button variant="ghost" onClick={() => { setSetupPinOpen(false); setSetupPin(""); setConfirmSetupPin(""); setShowSetupPin(false); }} className="rounded-full">
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Update PIN Dialog */}
          <Dialog open={updatePinOpen} onOpenChange={setUpdatePinOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Change Withdrawal PIN</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Current PIN</Label>
                  <div className="relative">
                    <Input
                      type={showPin ? "text" : "password"}
                      inputMode="numeric"
                      maxLength={6}
                      value={oldPin}
                      onChange={e => setOldPin(e.target.value.replace(/\D/g, ""))}
                      placeholder="Enter current PIN"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">New PIN</Label>
                  <div className="relative">
                    <Input
                      type={showPin ? "text" : "password"}
                      inputMode="numeric"
                      maxLength={6}
                      value={newPin}
                      onChange={e => setNewPin(e.target.value.replace(/\D/g, ""))}
                      placeholder="4–6 digits"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPin(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPin ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Confirm New PIN</Label>
                  <Input
                    type={showPin ? "text" : "password"}
                    inputMode="numeric"
                    maxLength={6}
                    value={confirmNewPin}
                    onChange={e => setConfirmNewPin(e.target.value.replace(/\D/g, ""))}
                    placeholder="Repeat new PIN"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={async () => {
                      if (newPin.length < 4) { toast.error("PIN must be at least 4 digits"); return; }
                      if (newPin !== confirmNewPin) { toast.error("New PINs do not match"); return; }
                      setUpdatingPin(true);
                      try {
                        await walletApi.updatePin(oldPin, newPin);
                        toast.success("PIN updated successfully");
                        setUpdatePinOpen(false);
                        setOldPin(""); setNewPin(""); setConfirmNewPin(""); setShowPin(false);
                        loadData();
                      } catch (err) {
                        const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Failed to update PIN";
                        toast.error(msg);
                      } finally {
                        setUpdatingPin(false);
                      }
                    }}
                    disabled={updatingPin}
                    className="rounded-full"
                  >
                    {updatingPin ? "Updating…" : "Update PIN"}
                  </Button>
                  <Button variant="ghost" onClick={() => { setUpdatePinOpen(false); setOldPin(""); setNewPin(""); setConfirmNewPin(""); }} className="rounded-full">
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Withdrawal history */}
          <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft mb-8">
            <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
              <Receipt className="size-4 text-primary" /> Withdrawal History
            </h3>
            {withdrawals.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No withdrawal requests yet.</p>
            ) : (
              <div className="space-y-3">
                {withdrawals.map(wd => {
                  const isPending = wd.status === "pending";
                  const isApproved = wd.status === "approved";
                  return (
                    <div key={wd.id} className="flex items-center justify-between rounded-xl bg-accent/20 px-3 py-2.5">
                      <div className="flex items-center gap-3">
                        <div className={`size-8 rounded-full flex items-center justify-center ${isApproved ? "bg-green-100 text-green-600" : isPending ? "bg-amber-100 text-amber-600" : "bg-red-100 text-red-600"}`}>
                          {isApproved ? <ArrowDownLeft className="size-4" /> : isPending ? <Clock className="size-4" /> : <ArrowUpRight className="size-4" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium">Withdrawal to {wd.bankName}</p>
                          <p className="text-[10px] text-muted-foreground">{wd.accountName} • {wd.accountNumber}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">{formatNaira(wd.amount)}</p>
                        <p className="text-[10px] text-muted-foreground">Fee: {formatNaira(wd.fee)} • Net: {formatNaira(wd.netAmount)}</p>
                        <p className={`text-[10px] font-medium ${isApproved ? "text-green-600" : isPending ? "text-amber-600" : "text-red-600"}`}>
                          {isPending ? "Pending" : isApproved ? "Approved" : "Rejected"}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Transaction history */}
          <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
            <h3 className="font-semibold text-sm mb-4">Transaction History</h3>
            {ledger.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No transactions yet.</p>
            ) : (
              <div className="space-y-3">
                {ledger.map(entry => {
                  const isCredit = entry.direction === "credit";
                  const isPending = entry.direction === "pending";
                  return (
                    <div key={entry.id} className="flex items-center justify-between rounded-xl bg-accent/20 px-3 py-2.5">
                      <div className="flex items-center gap-3">
                        <div className={`size-8 rounded-full flex items-center justify-center ${isCredit ? "bg-green-100 text-green-600" : isPending ? "bg-amber-100 text-amber-600" : "bg-red-100 text-red-600"}`}>
                          {isCredit ? <ArrowDownLeft className="size-4" /> : isPending ? <Clock className="size-4" /> : <ArrowUpRight className="size-4" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{entry.description || "Transaction"}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {isPending ? "Awaiting payment" : new Date(entry.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-semibold ${isCredit ? "text-green-600" : isPending ? "text-amber-600" : "text-red-600"}`}>
                          {isCredit ? "+" : isPending ? "" : "-"}{formatNaira(entry.amount)}
                          {isPending && <span className="ml-1 text-[10px] font-normal">(pending)</span>}
                        </p>
                        <p className="text-[10px] text-muted-foreground">Bal: {formatNaira(entry.currentBalance)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
